import { useCallback, useMemo } from 'react';
import I18n from 'I18n';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import TimePickerButtonV2 from 'MeetingsLib/components/schedule/time/TimePickerButtonV2';
import TimezoneSelect from 'MeetingsLib/components/schedule/time/TimezoneSelect';
import ZeroState from 'MeetingsLib/components/schedule/ZeroState';
import { getEvent as getEventSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getAvailableStartTimesForSelectedDate as getAvailableStartTimesForSelectedDateSelector } from 'MeetingsLib/selectors/AvailabilitySelectors';
import { getIsFetchingPublicAvailability } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { isTroubleshootingSelector, getTroubleshootStartTimesForSelectedDate } from 'MeetingsLib/selectors/TroubleshootingSelectors';
import { useAvailableTimesForSelectedDate } from '../../../context/PublicAvailabilityInfoContext';
import * as FormActions from 'MeetingsLib/redux/actions/FormActions';
import isEmpty from 'transmute/isEmpty';
import { Text } from 'foundations-components/components/Text';
import { H5 } from 'foundations-components/transitional/components/H5';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// TODO: Function component can use useSelector hook instead of connect
function TimePicker({
  branding,
  compact,
  profile,
  availableStartTimeOptions,
  duration,
  selectedTime,
  setEventDateTime,
  setTimezone,
  troubleshootStartTimes,
  userTimezone,
  isTroubleshooting,
  TimePickerMarker,
  selectedDate,
  isRescheduling
}) {
  const availableStartTimesFromContext = useAvailableTimesForSelectedDate();
  const sortedStartTimeOptions = useMemo(() => {
    const startTimesOptions = availableStartTimesFromContext.size ? availableStartTimesFromContext : availableStartTimeOptions;
    return startTimesOptions.sort();
  }, [availableStartTimeOptions, availableStartTimesFromContext]);
  const handleSetEventDateTime = useCallback(time => () => setEventDateTime(time), [setEventDateTime]);
  const shouldShowNoTimesMessage = useMemo(() => {
    if (isTroubleshooting) {
      return isEmpty(troubleshootStartTimes);
    }
    return availableStartTimeOptions.count() === 0;
  }, [availableStartTimeOptions, isTroubleshooting, troubleshootStartTimes]);
  const renderedTroubleshootTimeButtons = useMemo(() => {
    return troubleshootStartTimes.map(troubleshootStartTime => {
      const {
        conflictingEvents: [conflictingEvent = {}]
      } = troubleshootStartTime;
      return /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(TimePickerButtonV2, {
          branding: branding,
          disabled: !troubleshootStartTime.available,
          selectedTime: selectedTime !== null && selectedTime !== void 0 ? selectedTime : undefined,
          time: troubleshootStartTime.startMillisUtc,
          unavailableReason: troubleshootStartTime.reason,
          userTimezone: userTimezone,
          handleSetEventDateTime: handleSetEventDateTime(troubleshootStartTime.startMillisUtc),
          conflictingEvent: conflictingEvent,
          selectedDate: selectedDate
        })
      }, troubleshootStartTime.startMillisUtc);
    });
  }, [branding, handleSetEventDateTime, selectedDate, selectedTime, troubleshootStartTimes, userTimezone]);
  const renderTimePickerButtons = useMemo(() => {
    return sortedStartTimeOptions.map(time => {
      return /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(TimePickerButtonV2, {
          disabled: false,
          branding: branding,
          handleSetEventDateTime: handleSetEventDateTime(time),
          selectedTime: selectedTime !== null && selectedTime !== void 0 ? selectedTime : undefined,
          time: time,
          userTimezone: userTimezone,
          selectedDate: selectedDate
        })
      }, time);
    });
  }, [sortedStartTimeOptions, branding, handleSetEventDateTime, selectedDate, selectedTime, userTimezone]);
  const renderedInputs = useMemo(() => {
    const buttons = isTroubleshooting ? renderedTroubleshootTimeButtons : renderTimePickerButtons;
    return /*#__PURE__*/_jsx(List, {
      "aria-labelledby": "available-times",
      id: "available-times-region",
      paddingInline: "100",
      overflowY: "auto",
      flex: "1",
      minBlockSize: "0",
      inlineSize: "100%",
      children: buttons
    });
  }, [isTroubleshooting, renderTimePickerButtons, renderedTroubleshootTimeButtons]);
  const renderedTimezoneSelect = useMemo(() => {
    return /*#__PURE__*/_jsx(TimezoneSelect, {
      setTimezone: setTimezone,
      userTimezone: userTimezone
    });
  }, [setTimezone, userTimezone]);
  if (!duration) {
    return null;
  }
  if (shouldShowNoTimesMessage) {
    return /*#__PURE__*/_jsx(TFlex, {
      align: "center",
      paddingInline: "1000",
      blockSize: "100%",
      marginBlockStart: "1000",
      paddingBlock: "1000",
      children: /*#__PURE__*/_jsx(ZeroState, {
        profile: profile,
        TimePickerMarker: TimePickerMarker
      })
    });
  }
  return /*#__PURE__*/_jsxs(TFlex, {
    inlineSize: "100%",
    paddingInline: "800",
    marginBlockStart: compact ? '1000' : '800',
    "data-test-id": "time-options",
    flexDirection: "column",
    flex: "1",
    minBlockSize: "0",
    children: [/*#__PURE__*/_jsx(H5, {
      "aria-level": 2,
      id: "available-times",
      marginBlockEnd: "100",
      children: isRescheduling ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.timePicker.rescheduleHeader"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "prebook.timePicker.header"
      })
    }), /*#__PURE__*/_jsx(Text, {
      typography: "body-200",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "prebook.timePicker.headerDate",
        options: {
          currentDate: selectedDate ? I18n.moment(selectedDate).format('LL') : ''
        }
      })
    }), renderedTimezoneSelect, renderedInputs, /*#__PURE__*/_jsx(TimePickerMarker, {})]
  });
}
const mapStateToProps = state => ({
  activeLocale: state.activeLocale,
  troubleshootStartTimes: getTroubleshootStartTimesForSelectedDate(state),
  isTroubleshooting: isTroubleshootingSelector(state),
  availableStartTimeOptions: getAvailableStartTimesForSelectedDateSelector(state),
  duration: getEventSelector(state).duration,
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  selectedTime: getEventSelector(state).dateTime,
  selectedDate: getEventSelector(state).dateString,
  userTimezone: state.userTimezone,
  isRescheduling: state.isRescheduling
});
export default connect(mapStateToProps, Object.assign({}, FormActions))(TimePicker);